import { createClient } from '@supabase/supabase-js';

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
    console.error("Supabase URL or Anon Key is missing. Please check your .env file.");
}
export const supabase = createClient(supabaseUrl, supabaseAnonKey);

const getAuthHeader = async (isFormData = false) => {
    const { data: { session } } = await supabase.auth.getSession();
    const headers = {};
    if (session) {
        headers['Authorization'] = `Bearer ${session.access_token}`;
    }
    if (!isFormData) {
        headers['Content-Type'] = 'application/json';
    }
    return headers;
};

const handleResponse = async (res) => {
    if (!res.ok) {
        const errorText = await res.text();
        let errorMessage;
        
        try {
            // Try to parse the error as JSON to get the "detail" field
            const errorJson = JSON.parse(errorText);
            
            // Check if it's a FastAPI validation array (422 Unprocessable Entity)
            if (Array.isArray(errorJson.detail)) {
                errorMessage = errorJson.detail.map(err => `${err.loc.join('.')} - ${err.msg}`).join(', ');
            } else {
                errorMessage = errorJson.detail || 'An unknown error occurred';
            }
        } catch (e) {
            // If parsing fails, fallback to the raw text or status
            errorMessage = errorText || res.statusText;
        }
        
        throw new Error(errorMessage);
    }
    
    // For DELETE requests with no content
    if (res.status === 204) {
        return;
    }
    if (res.headers.get('Content-Type')?.includes('application/json')) {
        return res.json();
    }
    if (res.headers.get('Content-Type')?.includes('application/pdf') || res.headers.get('Content-Type')?.includes('text/plain')) {
        return res.blob();
    }
    return res;
};

// --- Equipment library cache ---------------------------------------------------
// The user + admin equipment libraries almost never change but were refetched on
// every mount of the Rack Builder and Wire Diagram. Cache them briefly in memory;
// mutation handlers call api.invalidateLibraryCache() and the whole thing self-
// expires after LIBRARY_CACHE_TTL_MS as a backstop.
const LIBRARY_CACHE_TTL_MS = 30000;
const _libraryCache = { user: null, admin: null };

const _cachedFetch = async (slot, loader) => {
    const hit = _libraryCache[slot];
    if (hit && Date.now() - hit.at < LIBRARY_CACHE_TTL_MS) {
        return hit.data;
    }
    const data = await loader();
    _libraryCache[slot] = { at: Date.now(), data };
    return data;
};

// Pass-through that clears the library cache after a mutation resolves.
const _bustLib = (result) => {
    _libraryCache.user = null;
    _libraryCache.admin = null;
    return result;
};

export const api = {
    invalidateLibraryCache: () => {
        _libraryCache.user = null;
        _libraryCache.admin = null;
    },
    // --- Shows ---
    getShows: async () => fetch('/api/shows', { headers: await getAuthHeader() }).then(handleResponse),
    getShow: async (showId) => fetch(`/api/shows/${showId}`, { headers: await getAuthHeader() }).then(handleResponse),
    getShowByName: async (showName) => fetch(`/api/shows/by-name/${showName}`, { headers: await getAuthHeader() }).then(handleResponse),
    
    saveShow: async (showId, data) => fetch(`/api/shows/${showId}`, {
        method: 'PUT',
        headers: await getAuthHeader(),
        body: JSON.stringify(data),
    }).then(handleResponse),
    
    createShow: async (data) => fetch('/api/shows', {
        method: 'POST',
        headers: await getAuthHeader(),
        body: JSON.stringify(data),
    }).then(handleResponse),
    
    deleteShow: async (showId) => fetch(`/api/shows/${showId}`, { 
        method: 'DELETE', 
        headers: await getAuthHeader() 
    }).then(res => { if (!res.ok) throw new Error("Delete failed") }),
    
    // --- Collaboration ---
    getCollaborators: async (showId) => fetch(`/api/shows/${showId}/collaborators`, { headers: await getAuthHeader() }).then(handleResponse),
    
    inviteCollaborator: async (showId, email, role) => fetch(`/api/shows/${showId}/collaborators`, {
        method: 'POST',
        headers: await getAuthHeader(),
        body: JSON.stringify({ email, role }),
    }).then(handleResponse),
    
    updateCollaboratorRole: async (showId, userId, role) => fetch(`/api/shows/${showId}/collaborators/${userId}`, {
        method: 'PUT',
        headers: await getAuthHeader(),
        body: JSON.stringify({ role }),
    }).then(handleResponse),
    
    removeCollaborator: async (showId, userId) => fetch(`/api/shows/${showId}/collaborators/${userId}`, {
        method: 'DELETE',
        headers: await getAuthHeader(),
    }).then(handleResponse),

    // --- Assets & Feedback ---
    uploadLogo: async (formData) => fetch('/api/upload/logo', {
        method: 'POST',
        headers: await getAuthHeader(true),
        body: formData,
    }).then(handleResponse),
    
    generatePdf: async (type, body) => fetch(`/api/pdf/${type}-labels`, {
        method: 'POST',
        headers: await getAuthHeader(),
        body: JSON.stringify(body),
    }).then(handleResponse),
    
    generateHoursPdf: async (body) => fetch('/api/pdf/hours-labels', {
        method: 'POST',
        headers: await getAuthHeader(),
        body: JSON.stringify(body),
    }).then(handleResponse),
    
    submitFeedback: async (data) => fetch('/api/feedback', {
        method: 'POST',
        headers: await getAuthHeader(),
        body: JSON.stringify(data),
    }).then(handleResponse),
    
    // --- User & Profile ---
    getProfile: async () => fetch('/api/profile', { headers: await getAuthHeader() }).then(handleResponse),
    
    updateProfile: async (profileData) => fetch('/api/profile', {
        method: 'POST',
        headers: await getAuthHeader(),
        body: JSON.stringify(profileData),
    }).then(handleResponse),
    
    getSsoConfig: async () => fetch('/api/sso_config', { headers: await getAuthHeader() }).then(handleResponse),
    
    updateSsoConfig: async (ssoData) => fetch('/api/sso_config', {
        method: 'POST',
        headers: await getAuthHeader(),
        body: JSON.stringify(ssoData),
    }).then(handleResponse),
    
    deleteAccount: async () => fetch('/api/profile', { method: 'DELETE', headers: await getAuthHeader() }),
    
    checkStatus: async (email) => fetch('/api/check-status', {
        method: 'POST',
        headers: await getAuthHeader(),
        body: JSON.stringify({ email }),
    }).then(handleResponse),
    
    // --- Racks & Equipment ---
    createRack: async (rackData) => fetch('/api/racks', { 
        method: 'POST', 
        headers: await getAuthHeader(), 
        body: JSON.stringify(rackData) 
    }).then(handleResponse),
    
    getRacksForShow: async (showId) => fetch(`/api/shows/${showId}/racks`, { headers: await getAuthHeader() }).then(handleResponse),
    getDetailedRacksForShow: async (showId) => fetch(`/api/shows/${showId}/detailed_racks`, { headers: await getAuthHeader() }).then(handleResponse),
    getRackDetails: async (rackId) => fetch(`/api/racks/${rackId}`, { headers: await getAuthHeader() }).then(handleResponse),
    
    updateRack: async (rackId, rackData) => fetch(`/api/racks/${rackId}`, { 
        method: 'PUT', 
        headers: await getAuthHeader(), 
        body: JSON.stringify(rackData) 
    }).then(handleResponse),
    
    deleteRack: async (rackId) => fetch(`/api/racks/${rackId}`, { method: 'DELETE', headers: await getAuthHeader() }),
    
    addEquipmentToRack: async (rackId, equipmentData) => fetch(`/api/racks/${rackId}/equipment`, { 
        method: 'POST', 
        headers: await getAuthHeader(), 
        body: JSON.stringify(equipmentData) 
    }).then(handleResponse),
    
    getEquipmentTemplates: async () => fetch('/api/equipment', { headers: await getAuthHeader() }).then(handleResponse),
    getLibrary: async () => _cachedFetch('user', async () => fetch('/api/library', { headers: await getAuthHeader() }).then(handleResponse)),
    
    moveEquipmentInRack: async (instanceId, newPositionData) => fetch(`/api/racks/equipment/${instanceId}`, { 
        method: 'PUT', 
        headers: await getAuthHeader(), 
        body: JSON.stringify(newPositionData) 
    }).then(handleResponse),
    
    deleteEquipmentFromRack: async (instanceId) => fetch(`/api/racks/equipment/${instanceId}`, { method: 'DELETE', headers: await getAuthHeader() }),
    getEquipmentInstance: async (instanceId) => fetch(`/api/racks/equipment/${instanceId}`, { headers: await getAuthHeader() }).then(handleResponse),
    
    createEquipmentInstance: async (instanceData) => fetch('/api/equipment_instances', { 
        method: 'POST', 
        headers: await getAuthHeader(), 
        body: JSON.stringify(instanceData) 
    }).then(handleResponse),
    
    updateEquipmentInstance: async (instanceId, updateData) => fetch(`/api/racks/equipment/${instanceId}`, { 
        method: 'PUT', 
        headers: await getAuthHeader(), 
        body: JSON.stringify(updateData) 
    }).then(handleResponse),
    
    getLibraryRacks: async () => fetch(`/api/racks?from_library=true`, { headers: await getAuthHeader() }).then(handleResponse),
    
    copyRackFromLibrary: async (rackId, showId, newRackName) => fetch('/api/racks/load_from_library', { 
        method: 'POST', 
        headers: await getAuthHeader(), 
        body: JSON.stringify({ template_rack_id: rackId, show_id: showId, new_rack_name: newRackName }) 
    }).then(handleResponse),
    
    getUnassignedEquipment: async (showId) => fetch(`/api/shows/${showId}/unassigned_equipment`, { headers: await getAuthHeader() }).then(handleResponse),
    
    generateRacksPdf: async (payload) => fetch('/api/pdf/racks', { 
        method: 'POST', 
        headers: await getAuthHeader(), 
        body: JSON.stringify(payload) 
    }).then(handleResponse),

    // --- User Panel Builder ---
    getPanelFolders: async () => fetch('/api/panels/folders', { headers: await getAuthHeader() }).then(handleResponse),
    createPanelFolder: async (data) => fetch('/api/panels/folders', { method: 'POST', headers: await getAuthHeader(), body: JSON.stringify(data) }).then(handleResponse),
    deletePanelFolder: async (id) => fetch(`/api/panels/folders/${id}`, { method: 'DELETE', headers: await getAuthHeader() }).then(handleResponse),
    getPanelTemplates: async () => fetch('/api/panels/templates', { headers: await getAuthHeader() }).then(handleResponse),
    createPanelTemplate: async (data) => fetch('/api/panels/templates', { method: 'POST', headers: await getAuthHeader(), body: JSON.stringify(data) }).then(handleResponse),
    updatePanelTemplate: async (id, data) => fetch(`/api/panels/templates/${id}`, { method: 'PUT', headers: await getAuthHeader(), body: JSON.stringify(data) }).then(handleResponse),
    deletePanelTemplate: async (id) => fetch(`/api/panels/templates/${id}`, { method: 'DELETE', headers: await getAuthHeader() }).then(handleResponse),
    getPanelInstances: async (panelInstanceId) => fetch(`/api/panels/instances/${panelInstanceId}`, { headers: await getAuthHeader() }).then(handleResponse),
    createPanelInstance: async (data) => fetch('/api/panels/instances', { method: 'POST', headers: await getAuthHeader(), body: JSON.stringify(data) }).then(handleResponse),
    updatePanelInstance: async (id, data) => fetch(`/api/panels/instances/${id}`, { method: 'PUT', headers: await getAuthHeader(), body: JSON.stringify(data) }).then(handleResponse),
    deletePanelInstance: async (id) => fetch(`/api/panels/instances/${id}`, { method: 'DELETE', headers: await getAuthHeader() }).then(handleResponse),
    exportPanelsPdf: async (showId) => fetch(`/api/panels/shows/${showId}/export`, { headers: await getAuthHeader() }).then(handleResponse),
    getAllPanelInstancesForShow: async (showId) => fetch(`/api/panels/shows/${showId}/panel-instances`, { headers: await getAuthHeader() }).then(handleResponse),

    // --- Admin Panel Library ---
    createAdminPanelFolder: async (data) => fetch('/api/panels/admin/folders', { method: 'POST', headers: await getAuthHeader(), body: JSON.stringify(data) }).then(handleResponse),
    deleteAdminPanelFolder: async (id) => fetch(`/api/panels/admin/folders/${id}`, { method: 'DELETE', headers: await getAuthHeader() }).then(handleResponse),
    createAdminPanelTemplate: async (data) => fetch('/api/panels/admin/templates', { method: 'POST', headers: await getAuthHeader(), body: JSON.stringify(data) }).then(handleResponse),
    updateAdminPanelTemplate: async (id, data) => fetch(`/api/panels/admin/templates/${id}`, { method: 'PUT', headers: await getAuthHeader(), body: JSON.stringify(data) }).then(handleResponse),
    deleteAdminPanelTemplate: async (id) => fetch(`/api/panels/admin/templates/${id}`, { method: 'DELETE', headers: await getAuthHeader() }).then(handleResponse),

    // --- Admin Equipment Library ---
    getAdminLibrary: async () => _cachedFetch('admin', async () => fetch('/api/admin/library', { headers: await getAuthHeader() }).then(handleResponse)),
    
    createAdminFolder: async (folderData) => fetch('/api/admin/folders', {
        method: 'POST',
        headers: await getAuthHeader(),
        body: JSON.stringify(folderData)
    }).then(handleResponse).then(_bustLib),

    updateAdminFolder: async (folderId, folderData) => fetch(`/api/admin/folders/${folderId}`, {
        method: 'PUT',
        headers: await getAuthHeader(),
        body: JSON.stringify(folderData)
    }).then(handleResponse).then(_bustLib),

    deleteAdminFolder: async (folderId) => fetch(`/api/admin/folders/${folderId}`, { method: 'DELETE', headers: await getAuthHeader() }).then(_bustLib),

    createAdminEquipment: async (equipmentData) => fetch('/api/admin/equipment', {
        method: 'POST',
        headers: await getAuthHeader(),
        body: JSON.stringify(equipmentData)
    }).then(handleResponse).then(_bustLib),

    updateAdminEquipment: async (equipmentId, equipmentData) => fetch(`/api/admin/equipment/${equipmentId}`, {
        method: 'PUT', 
        headers: await getAuthHeader(), 
        body: JSON.stringify(equipmentData)
    }).then(handleResponse).then(_bustLib),

    deleteAdminEquipment: async (equipmentId) => fetch(`/api/admin/equipment/${equipmentId}`, { method: 'DELETE', headers: await getAuthHeader() }).then(_bustLib),

    // --- User Library ---
    createUserFolder: async (folderData) => fetch('/api/library/folders', {
        method: 'POST',
        headers: await getAuthHeader(),
        body: JSON.stringify(folderData)
    }).then(handleResponse).then(_bustLib),

    updateUserFolder: async (folderId, folderData) => fetch(`/api/library/folders/${folderId}`, {
        method: 'PUT',
        headers: await getAuthHeader(),
        body: JSON.stringify(folderData)
    }).then(handleResponse).then(_bustLib),

    deleteUserFolder: async (folderId) => fetch(`/api/library/folders/${folderId}`, { method: 'DELETE', headers: await getAuthHeader() }).then(_bustLib),

    createUserEquipment: async (equipmentData) => fetch('/api/library/equipment', {
        method: 'POST',
        headers: await getAuthHeader(),
        body: JSON.stringify(equipmentData)
    }).then(handleResponse).then(_bustLib),

    updateUserEquipment: async (equipmentId, equipmentData) => fetch(`/api/library/equipment/${equipmentId}`, {
        method: 'PUT',
        headers: await getAuthHeader(),
        body: JSON.stringify(equipmentData)
    }).then(handleResponse).then(_bustLib),

    deleteUserEquipment: async (equipmentId) => fetch(`/api/library/equipment/${equipmentId}`, { method: 'DELETE', headers: await getAuthHeader() }).then(_bustLib),

    copyEquipmentToLibrary: async (copyData) => fetch('/api/library/copy_equipment', {
        method: 'POST',
        headers: await getAuthHeader(),
        body: JSON.stringify(copyData)
    }).then(handleResponse).then(_bustLib),

    // --- Admin Switch Config ---
    getSwitchModels: async () => fetch('/api/v1/admin/switch_models', { headers: await getAuthHeader() }).then(handleResponse),
    
    createSwitchModel: async (modelData) => fetch('/api/v1/admin/switch_models', { 
        method: 'POST', 
        headers: await getAuthHeader(), 
        body: JSON.stringify(modelData) 
    }).then(handleResponse),
    
    updateSwitchModel: async (modelId, modelData) => fetch(`/api/v1/admin/switch_models/${modelId}`, { 
        method: 'PUT', 
        headers: await getAuthHeader(), 
        body: JSON.stringify(modelData) 
    }).then(handleResponse),
    
    deleteSwitchModel: async (modelId) => fetch(`/api/v1/admin/switch_models/${modelId}`, { 
        method: 'DELETE', 
        headers: await getAuthHeader(), 
    }),
    
    linkEquipmentToModel: async (equipmentId, modelId) => fetch(`/api/v1/admin/equipment/${equipmentId}/link_model`, { 
        method: 'PUT', 
        headers: await getAuthHeader(), 
        body: JSON.stringify({ switch_model_id: modelId }), 
    }).then(handleResponse),

    // --- User Switch Config ---
    getConfigurableSwitches: async (showId) => fetch(`/api/v1/switches?show_id=${showId}`, { headers: await getAuthHeader() }).then(handleResponse),

    createSwitchConfig: async (rackItemId) => fetch('/api/v1/switches', {
        method: 'POST',
        headers: await getAuthHeader(),
        body: JSON.stringify({ rack_item_id: rackItemId }),
    }).then(handleResponse),

    getSwitchDetails: async (switchId) => fetch(`/api/v1/switches/${switchId}/details`, { headers: await getAuthHeader() }).then(handleResponse),
    getSwitchConfig: async (switchId) => fetch(`/api/v1/switches/${switchId}/config`, { headers: await getAuthHeader() }).then(handleResponse),

    deleteSwitchConfig: async (switchId) => fetch(`/api/v1/switches/${switchId}`, {
        method: 'DELETE',
        headers: await getAuthHeader(),
    }).then(handleResponse),

    saveSwitchPortConfig: async (switchId, portConfigData) => fetch(`/api/v1/switches/${switchId}/config`, {
        method: 'PUT',
        headers: await getAuthHeader(),
        body: JSON.stringify(portConfigData),
    }).then(handleResponse),

    saveSwitchLagConfig: async (switchId, lagConfigData) => fetch(`/api/v1/switches/${switchId}/lag_config`, {
        method: 'PUT',
        headers: await getAuthHeader(),
        body: JSON.stringify(lagConfigData),
    }).then(handleResponse),

    saveSwitchDeviceSettings: async (switchId, settings) => fetch(`/api/v1/switches/${switchId}/device_settings`, {
        method: 'PUT',
        headers: await getAuthHeader(),
        body: JSON.stringify(settings),
    }).then(handleResponse),

    saveSwitchManagementIp: async (switchId, managementIp) => fetch(`/api/v1/switches/${switchId}/management_ip`, {
        method: 'PUT',
        headers: await getAuthHeader(),
        body: JSON.stringify({ management_ip: managementIp }),
    }).then(handleResponse),

    // Ordered REST steps for the browser to execute directly against the switch
    // (Local Network Access), and the CLI command list + baud rate for the Web
    // Serial console executor. Neither the plan itself nor the switch's credentials
    // ever involve a call back to ShowReady beyond fetching the plan.
    getSwitchRestPlan: async (switchId) => fetch(`/api/v1/switches/${switchId}/rest_plan`, { headers: await getAuthHeader() }).then(handleResponse),
    getSwitchCliCommands: async (switchId) => fetch(`/api/v1/switches/${switchId}/cli_commands`, { headers: await getAuthHeader() }).then(handleResponse),

    getSwitchDrivers: async () => fetch('/api/v1/switch_drivers', { headers: await getAuthHeader() }).then(handleResponse),

    // --- Wire Diagram & Connections ---
    getConnectionsForShow: async (showId) => fetch(`/api/shows/${showId}/connections`, { headers: await getAuthHeader() }).then(handleResponse),
    getConnectionsForDevice: async (instanceId) => fetch(`/api/equipment/${instanceId}/connections`, { headers: await getAuthHeader() }).then(handleResponse),
    
    createConnection: async (connectionData) => fetch('/api/connections', { 
        method: 'POST', 
        headers: await getAuthHeader(), 
        body: JSON.stringify(connectionData), 
    }).then(handleResponse),
    
    updateConnection: async (connectionId, updateData) => fetch(`/api/connections/${connectionId}`, { 
        method: 'PUT', 
        headers: await getAuthHeader(), 
        body: JSON.stringify(updateData), 
    }).then(handleResponse),
    
    deleteConnection: async (connectionId) => fetch(`/api/connections/${connectionId}`, { method: 'DELETE', headers: await getAuthHeader() }).then(handleResponse),

    // Wire Diagram pages + bulk placement (app/routers/wire_diagram.py)
    getWireDiagramPages: async (showId) => fetch(`/api/v1/shows/${showId}/wire-diagram/pages`, { headers: await getAuthHeader() }).then(handleResponse),
    createWireDiagramPage: async (showId, name = null) => fetch(`/api/v1/shows/${showId}/wire-diagram/pages`, {
        method: 'POST',
        headers: await getAuthHeader(),
        body: JSON.stringify({ name }),
    }).then(handleResponse),
    renameWireDiagramPage: async (showId, pageNumber, name) => fetch(`/api/v1/shows/${showId}/wire-diagram/pages/${pageNumber}`, {
        method: 'PATCH',
        headers: await getAuthHeader(),
        body: JSON.stringify({ name }),
    }).then(handleResponse),
    reorderWireDiagramPages: async (showId, pageNumbers) => fetch(`/api/v1/shows/${showId}/wire-diagram/pages-order`, {
        method: 'PUT',
        headers: await getAuthHeader(),
        body: JSON.stringify({ page_numbers: pageNumbers }),
    }).then(handleResponse),
    deleteWireDiagramPage: async (showId, pageNumber, deleteConnections = false) => fetch(`/api/v1/shows/${showId}/wire-diagram/pages/${pageNumber}?delete_connections=${deleteConnections ? 'true' : 'false'}`, {
        method: 'DELETE',
        headers: await getAuthHeader(),
    }).then(handleResponse),
    saveWireDiagramPositions: async (showId, items) => fetch(`/api/v1/shows/${showId}/wire-diagram/positions`, {
        method: 'PUT',
        headers: await getAuthHeader(),
        body: JSON.stringify({ items }),
    }).then(handleResponse),
    getWireDiagramModuleTemplates: async (showId) => fetch(`/api/v1/shows/${showId}/wire-diagram/module-templates`, { headers: await getAuthHeader() }).then(handleResponse),
    saveWirePortLayout: async (showId, instanceId, portLayout) => fetch(`/api/v1/shows/${showId}/wire-diagram/port-layout/${instanceId}`, {
        method: 'PUT',
        headers: await getAuthHeader(),
        body: JSON.stringify({ port_layout: portLayout }),
    }).then(handleResponse),
    unplaceWireDiagramEquipment: async (showId, instanceIds, deleteConnections = false) => fetch(`/api/v1/shows/${showId}/wire-diagram/unplace`, {
        method: 'POST',
        headers: await getAuthHeader(),
        body: JSON.stringify({ instance_ids: instanceIds, delete_connections: deleteConnections }),
    }).then(handleResponse),

    exportWirePdf: async (graphData, showId, titleBlockData) => fetch(`/api/export/wire.pdf?show_id=${encodeURIComponent(showId)}`, { 
        method: 'POST', 
        headers: await getAuthHeader(), 
        body: JSON.stringify({graph: graphData, title_block: titleBlockData}), 
    }).then(handleResponse),

    // --- Loom Builder ---
    getLoomsForShow: async (showId) => fetch(`/api/shows/${showId}/looms`, { headers: await getAuthHeader() }).then(handleResponse),
    
    createLoom: async (loomData) => fetch('/api/looms', { 
        method: 'POST', 
        headers: await getAuthHeader(), 
        body: JSON.stringify(loomData), 
    }).then(handleResponse),
    
    updateLoom: async (loomId, loomData) => fetch(`/api/looms/${loomId}`, { 
        method: 'PUT', 
        headers: await getAuthHeader(), 
        body: JSON.stringify(loomData), 
    }).then(handleResponse),
    
    deleteLoom: async (loomId) => fetch(`/api/looms/${loomId}`, { method: 'DELETE', headers: await getAuthHeader(), }),
    
    copyLoom: async (loomId, newName) => fetch(`/api/looms/${loomId}/copy`, { 
        method: 'POST', 
        headers: await getAuthHeader(), 
        body: JSON.stringify({ new_name: newName }), 
    }).then(handleResponse),
    
    getCablesForLoom: async (loomId) => fetch(`/api/looms/${loomId}/cables`, { headers: await getAuthHeader() }).then(handleResponse),
    
    createCable: async (cableData) => fetch('/api/cables', { 
        method: 'POST', 
        headers: await getAuthHeader(), 
        body: JSON.stringify(cableData), 
    }).then(handleResponse),
    
    updateCable: async (cableId, cableData) => fetch(`/api/cables/${cableId}`, { 
        method: 'PUT', 
        headers: await getAuthHeader(), 
        body: JSON.stringify(cableData), 
    }).then(handleResponse),
    
    deleteCable: async (cableId) => fetch(`/api/cables/${cableId}`, { method: 'DELETE', headers: await getAuthHeader(), }),
    
    bulkUpdateCables: async (updateData) => fetch('/api/cables/bulk-update', { 
        method: 'PUT', 
        headers: await getAuthHeader(), 
        body: JSON.stringify(updateData), 
    }).then(handleResponse),

    // --- VLANs ---
    getVlans: async (showId) => fetch(`/api/vlans/${showId}`, { headers: await getAuthHeader() }).then(handleResponse),
    
    createVlan: async (showId, vlanData) => fetch(`/api/vlans/${showId}`, { 
        method: 'POST', 
        headers: await getAuthHeader(), 
        body: JSON.stringify(vlanData), 
    }).then(handleResponse),
    
    updateVlan: async (vlanId, vlanData) => fetch(`/api/vlans/${vlanId}`, { 
        method: 'PUT', 
        headers: await getAuthHeader(), 
        body: JSON.stringify(vlanData), 
    }).then(handleResponse),
    
    deleteVlan: async (vlanId) => fetch(`/api/vlans/${vlanId}`, { method: 'DELETE', headers: await getAuthHeader(), }),
    
    generateVlanScript: async (showId, payload) => fetch(`/api/vlans/${showId}/generate-script`, { 
        method: 'POST', 
        headers: await getAuthHeader(), 
        body: JSON.stringify(payload), 
    }).then(handleResponse),

    // --- Network IPs ---
    getNetworkIps: async (showId) => fetch(`/api/v1/shows/${showId}/network/ips`, { headers: await getAuthHeader() }).then(handleResponse),
    createNetworkIp: async (showId, payload) => fetch(`/api/v1/shows/${showId}/network/ips`, { method: 'POST', headers: await getAuthHeader(), body: JSON.stringify(payload) }).then(handleResponse),
    updateNetworkIp: async (showId, ipId, payload) => fetch(`/api/v1/shows/${showId}/network/ips/${ipId}`, { method: 'PUT', headers: await getAuthHeader(), body: JSON.stringify(payload) }).then(handleResponse),
    deleteNetworkIp: async (showId, ipId) => fetch(`/api/v1/shows/${showId}/network/ips/${ipId}`, { method: 'DELETE', headers: await getAuthHeader() }).then(handleResponse),
    syncNetworkIpEntity: async (showId, payload) => fetch(`/api/v1/shows/${showId}/network/ips/sync-entity`, { method: 'POST', headers: await getAuthHeader(), body: JSON.stringify(payload) }).then(handleResponse),
    deleteNetworkIpEntity: async (showId, entityType, entityId) => fetch(`/api/v1/shows/${showId}/network/ips/entity/${entityType}/${entityId}`, { method: 'DELETE', headers: await getAuthHeader() }).then(handleResponse),
    // Subnets & DHCP: one row per VLAN, keyed by vlan id.
    getVlanSubnets: async (showId) => fetch(`/api/v1/shows/${showId}/network/subnets`, { headers: await getAuthHeader() }).then(handleResponse),
    saveVlanSubnet: async (showId, vlanId, payload) => fetch(`/api/v1/shows/${showId}/network/subnets/${vlanId}`, { method: 'PUT', headers: await getAuthHeader(), body: JSON.stringify(payload) }).then(handleResponse),
    deleteVlanSubnet: async (showId, vlanId) => fetch(`/api/v1/shows/${showId}/network/subnets/${vlanId}`, { method: 'DELETE', headers: await getAuthHeader() }).then(handleResponse),

    // --- Admin Metrics ---
    getMetrics: async () => fetch('/api/admin/metrics', { headers: await getAuthHeader() }).then(handleResponse),
    getOverallActivityStatus: async () => fetch('/api/admin/activity/status', { headers: await getAuthHeader() }).then(handleResponse),
    
    // --- Admin User Management ---
    getAllUsers: async (searchTerm) => {
        const url = searchTerm ? `/api/admin/users?search=${encodeURIComponent(searchTerm)}` : '/api/admin/users';
        return fetch(url, { headers: await getAuthHeader() }).then(handleResponse);
    },
    
    updateUserTier: async (userId, tier) => fetch(`/api/admin/users/${userId}/tier`, { 
        method: 'PUT', 
        headers: await getAuthHeader(), 
        body: JSON.stringify({ tier }), 
    }).then(handleResponse),
    
    updateUserEntitlement: async (userId, isFounding, isBeta) => fetch(`/api/admin/users/${userId}/entitlement`, { 
        method: 'PUT', 
        headers: await getAuthHeader(), 
        body: JSON.stringify({ is_founding: isFounding, is_beta: isBeta }), 
    }).then(handleResponse),
    
    deactivateUser: async (userId) => fetch(`/api/admin/users/${userId}/deactivate`, { method: 'POST', headers: await getAuthHeader(), }).then(handleResponse),
    reactivateUser: async (userId) => fetch(`/api/admin/users/${userId}/reactivate`, { method: 'POST', headers: await getAuthHeader(), }).then(handleResponse),
    
    startImpersonation: async (userId) => fetch('/api/admin/impersonate', { 
        method: 'POST', 
        headers: await getAuthHeader(), 
        body: JSON.stringify({ user_id: userId }), 
    }).then(handleResponse),
    
    stopImpersonation: async () => fetch('/api/admin/impersonate/stop', { method: 'POST', headers: await getAuthHeader(), }).then(handleResponse),
    
    // --- Admin Tier Limits ---
    getDetailedTiers: async () => fetch('/api/admin/tiers/detailed', { headers: await getAuthHeader() }).then(handleResponse),
    updateTierLimits: async (tierName, limits) => fetch(`/api/admin/tiers/${tierName}/limits`, {
        method: 'PUT',
        headers: await getAuthHeader(),
        body: JSON.stringify(limits),
    }).then(handleResponse),

    // --- Admin Email ---
    getAdminTiers: async () => fetch('/api/admin/tiers', { headers: await getAuthHeader() }).then(handleResponse),
    
    adminSendEmail: async (payload) => fetch('/api/admin/send-email', { 
        method: 'POST', 
        headers: await getAuthHeader(), 
        body: JSON.stringify(payload), 
    }).then(handleResponse),
    
    sendNewUserListEmail: async (payload) => fetch('/api/admin/send-new-user-list-email', { 
        method: 'POST', 
        headers: await getAuthHeader(), 
        body: JSON.stringify(payload), 
    }).then(handleResponse),

    // --- Sender Identity ---
    getSenderIdentities: async () => fetch('/api/admin/senders', { headers: await getAuthHeader() }).then(handleResponse),
    
    createSenderIdentity: async (data) => fetch('/api/admin/senders', { 
        method: 'POST', 
        headers: await getAuthHeader(), 
        body: JSON.stringify(data), 
    }).then(handleResponse),
    
    deleteSenderIdentity: async (id) => fetch(`/api/admin/senders/${id}`, { method: 'DELETE', headers: await getAuthHeader() }).then(handleResponse),

    // --- Email Templates ---
    getEmailTemplates: async (category) => {
        const url = category ? `/api/communications/templates?category=${encodeURIComponent(category)}` : '/api/communications/templates';
        return fetch(url, { headers: await getAuthHeader() }).then(handleResponse);
    },
    
    createEmailTemplate: async (data) => fetch('/api/communications/templates', { 
        method: 'POST', 
        headers: await getAuthHeader(), 
        body: JSON.stringify(data), 
    }).then(handleResponse),
    
    updateEmailTemplate: async (id, data) => fetch(`/api/communications/templates/${id}`, { 
        method: 'PUT', 
        headers: await getAuthHeader(), 
        body: JSON.stringify(data), 
    }).then(handleResponse),
    
    deleteEmailTemplate: async (id) => fetch(`/api/communications/templates/${id}`, { method: 'DELETE', headers: await getAuthHeader() }),
    
    restoreDefaultEmailTemplates: async () => fetch('/api/communications/templates/restore', { method: 'POST', headers: await getAuthHeader() }).then(handleResponse),
    
    sendCommunication: async (payload) => fetch('/api/communications/send', { 
        method: 'POST', 
        headers: await getAuthHeader(), 
        body: JSON.stringify(payload), 
    }).then(handleResponse),

    // --- Admin RBAC ---
    getAllFeatureRestrictions: async () => fetch('/api/admin/feature_restrictions', { headers: await getAuthHeader() }).then(handleResponse),
    
    updateFeatureRestriction: async (featureName, restrictionData) => fetch(`/api/admin/feature_restrictions/${featureName}`, { 
        method: 'PUT', 
        headers: await getAuthHeader(), 
        body: JSON.stringify(restrictionData), 
    }).then(handleResponse),

    // --- Permissions Versioning ---
    getPermissionsVersion: async () => fetch('/api/permissions/version', { headers: await getAuthHeader() }).then(handleResponse),

    // --- Roster ---
    getRoster: async () => fetch('/api/roster', { headers: await getAuthHeader() }).then(handleResponse),
    
    createRosterMember: async (rosterData) => fetch('/api/roster', { 
        method: 'POST', 
        headers: await getAuthHeader(), 
        body: JSON.stringify(rosterData), 
    }).then(handleResponse),
    
    updateRosterMember: async (rosterId, rosterData) => fetch(`/api/roster/${rosterId}`, { 
        method: 'PUT', 
        headers: await getAuthHeader(), 
        body: JSON.stringify(rosterData), 
    }).then(handleResponse),
    
    deleteRosterMember: async (rosterId) => fetch(`/api/roster/${rosterId}`, { method: 'DELETE', headers: await getAuthHeader(), }).then(handleResponse),

    getRosterMember: async (rosterId) => fetch(`/api/roster/${rosterId}`, { headers: await getAuthHeader() }).then(handleResponse),

    eraseRosterMember: async (rosterId) => fetch(`/api/roster/${rosterId}/erase`, {
        method: 'POST',
        headers: await getAuthHeader(),
    }).then(handleResponse),

    // --- Roster Tags (account catalog; see utils/rosterTags.js) ---
    getRosterTags: async () => fetch('/api/roster_tags', { headers: await getAuthHeader() }).then(handleResponse),

    createRosterTag: async (data) => fetch('/api/roster_tags', {
        method: 'POST',
        headers: await getAuthHeader(),
        body: JSON.stringify(data),
    }).then(handleResponse),

    updateRosterTag: async (tagId, data) => fetch(`/api/roster_tags/${tagId}`, {
        method: 'PUT',
        headers: await getAuthHeader(),
        body: JSON.stringify(data),
    }).then(handleResponse),

    mergeRosterTag: async (tagId, intoTagId) => fetch(`/api/roster_tags/${tagId}/merge`, {
        method: 'POST',
        headers: await getAuthHeader(),
        body: JSON.stringify({ into_tag_id: intoTagId }),
    }).then(handleResponse),

    deleteRosterTag: async (tagId) => fetch(`/api/roster_tags/${tagId}`, { method: 'DELETE', headers: await getAuthHeader() }).then(handleResponse),

    // --- Roster Custom Fields ---
    getRosterCustomFields: async () => fetch('/api/roster_custom_fields', { headers: await getAuthHeader() }).then(handleResponse),

    createRosterCustomField: async (data) => fetch('/api/roster_custom_fields', {
        method: 'POST',
        headers: await getAuthHeader(),
        body: JSON.stringify(data),
    }).then(handleResponse),

    updateRosterCustomField: async (fieldId, data) => fetch(`/api/roster_custom_fields/${fieldId}`, {
        method: 'PUT',
        headers: await getAuthHeader(),
        body: JSON.stringify(data),
    }).then(handleResponse),

    reorderRosterCustomFields: async (fieldIds) => fetch('/api/roster_custom_fields_order', {
        method: 'PUT',
        headers: await getAuthHeader(),
        body: JSON.stringify({ field_ids: fieldIds }),
    }).then(handleResponse),

    deleteRosterCustomField: async (fieldId) => fetch(`/api/roster_custom_fields/${fieldId}`, { method: 'DELETE', headers: await getAuthHeader(), }),

    // --- Crew Profile Links (owner side; see app/routers/profile_links.py) ---
    getProfileLinkSettings: async () => fetch('/api/v1/roster-profile/settings', { headers: await getAuthHeader() }).then(handleResponse),

    updateProfileLinkSettings: async (data) => fetch('/api/v1/roster-profile/settings', {
        method: 'PUT',
        headers: await getAuthHeader(),
        body: JSON.stringify(data),
    }).then(handleResponse),

    updateCustomFieldCrewAccess: async (fieldId, crewAccess) => fetch(`/api/v1/roster-profile/custom-fields/${fieldId}`, {
        method: 'PUT',
        headers: await getAuthHeader(),
        body: JSON.stringify({ crew_access: crewAccess }),
    }).then(handleResponse),

    getProfileLinkStatus: async (rosterId) => fetch(`/api/v1/roster/${rosterId}/profile-link`, { headers: await getAuthHeader() }).then(handleResponse),

    createProfileLink: async (rosterId) => fetch(`/api/v1/roster/${rosterId}/profile-link`, {
        method: 'POST',
        headers: await getAuthHeader(),
    }).then(handleResponse),

    revokeProfileLink: async (rosterId) => fetch(`/api/v1/roster/${rosterId}/profile-link`, {
        method: 'DELETE',
        headers: await getAuthHeader(),
    }).then(handleResponse),

    sendProfileUpdateRequests: async (data) => fetch('/api/v1/roster-profile/send', {
        method: 'POST',
        headers: await getAuthHeader(),
        body: JSON.stringify(data),
    }).then(handleResponse),

    getProfileChanges: async (rosterId) => fetch(`/api/v1/roster/${rosterId}/profile-changes`, { headers: await getAuthHeader() }).then(handleResponse),

    revertProfileChange: async (rosterId, changeId) => fetch(`/api/v1/roster/${rosterId}/profile-changes/${changeId}/revert`, {
        method: 'POST',
        headers: await getAuthHeader(),
    }).then(handleResponse),

    // --- Roster Onboarding Link ---
    getOnboardingLink: async () => fetch('/api/v1/onboarding-link', { headers: await getAuthHeader() }).then(handleResponse),

    updateOnboardingLink: async (data) => fetch('/api/v1/onboarding-link', {
        method: 'PUT',
        headers: await getAuthHeader(),
        body: JSON.stringify(data),
    }).then(handleResponse),

    regenerateOnboardingSlug: async () => fetch('/api/v1/onboarding-link/regenerate-slug', {
        method: 'POST',
        headers: await getAuthHeader(),
    }).then(handleResponse),

    // --- Show Crew ---
    getShowCrew: async (showId) => fetch(`/api/shows/${showId}/crew`, { headers: await getAuthHeader() }).then(handleResponse),
    
    updateShowSettings: async (showId, settings) => fetch(`/api/shows/${showId}/settings`, { 
        method: 'PUT', 
        headers: await getAuthHeader(), 
        body: JSON.stringify(settings), 
    }).then(handleResponse),
    
    addCrewToShow: async (showId, rosterId, details) => fetch(`/api/shows/${showId}/crew/${rosterId}`, { 
        method: 'POST', 
        headers: await getAuthHeader(), 
        body: JSON.stringify(details), 
    }).then(handleResponse),
    
    removeCrewFromShow: async (showId, showCrewId) => fetch(`/api/shows/${showId}/crew/${showCrewId}`, { method: 'DELETE', headers: await getAuthHeader(), }),
    
    createRosterMemberAndAddToShow: async (data) => fetch('/api/roster_and_show_crew', { 
        method: 'POST', 
        headers: await getAuthHeader(), 
        body: JSON.stringify(data), 
    }).then(handleResponse),
    
    updateShowCrewMember: async (showCrewId, data) => fetch(`/api/show_crew/${showCrewId}`, {
        method: 'PUT',
        headers: await getAuthHeader(),
        body: JSON.stringify(data),
    }).then(handleResponse),

    // --- Position rate card (per show) and each crew member's positions/rates on it.
    // Rates come back null for a viewer on a show whose owner hasn't shared rates. ---
    getRateCard: async (showId) => fetch(`/api/v1/shows/${showId}/positions`, {
        headers: await getAuthHeader(),
    }).then(handleResponse),

    createShowPosition: async (showId, data) => fetch(`/api/v1/shows/${showId}/positions`, {
        method: 'POST',
        headers: await getAuthHeader(),
        body: JSON.stringify(data),
    }).then(handleResponse),

    updateShowPosition: async (showId, positionId, data) => fetch(`/api/v1/shows/${showId}/positions/${positionId}`, {
        method: 'PUT',
        headers: await getAuthHeader(),
        body: JSON.stringify(data),
    }).then(handleResponse),

    deleteShowPosition: async (showId, positionId) => fetch(`/api/v1/shows/${showId}/positions/${positionId}`, {
        method: 'DELETE',
        headers: await getAuthHeader(),
    }).then(handleResponse),

    setRateSharing: async (showId, shareRates) => fetch(`/api/v1/shows/${showId}/rate-sharing`, {
        method: 'PUT',
        headers: await getAuthHeader(),
        body: JSON.stringify({ share_rates: shareRates }),
    }).then(handleResponse),

    addCrewPositions: async (showCrewId, positions) => fetch(`/api/v1/show-crew/${showCrewId}/positions`, {
        method: 'POST',
        headers: await getAuthHeader(),
        body: JSON.stringify(positions),
    }).then(handleResponse),

    updateCrewPositionRate: async (lineId, rate) => fetch(`/api/v1/show-crew-positions/${lineId}`, {
        method: 'PUT',
        headers: await getAuthHeader(),
        body: JSON.stringify({ rate }),
    }).then(handleResponse),

    deleteCrewPosition: async (lineId) => fetch(`/api/v1/show-crew-positions/${lineId}`, {
        method: 'DELETE',
        headers: await getAuthHeader(),
    }).then(handleResponse),

    // --- Schedule (availability calls / pool / assignment) ---
    sendAvailabilityCall: async (showId, data) => fetch(`/api/v1/shows/${showId}/availability-calls`, {
        method: 'POST',
        headers: await getAuthHeader(),
        body: JSON.stringify(data),
    }).then(handleResponse),

    getAvailabilityPool: async (showId) => fetch(`/api/v1/shows/${showId}/availability`, {
        headers: await getAuthHeader(),
    }).then(handleResponse),

    assignFromAvailability: async (responseId, data) => fetch(`/api/v1/availability/${responseId}/assign`, {
        method: 'POST',
        headers: await getAuthHeader(),
        body: JSON.stringify(data),
    }).then(handleResponse),

    // Direct assignment straight from the account-wide Roster (no availability-call
    // response involved) — e.g. dragging a name onto a date in the global Scheduling grid.
    assignRosterMember: async (showId, rosterId, data) => fetch(`/api/v1/shows/${showId}/crew/${rosterId}/assign`, {
        method: 'POST',
        headers: await getAuthHeader(),
        body: JSON.stringify(data),
    }).then(handleResponse),

    updateAvailabilityStatus: async (responseId, status) => fetch(`/api/v1/availability/${responseId}`, {
        method: 'PATCH',
        headers: await getAuthHeader(),
        body: JSON.stringify({ status }),
    }).then(handleResponse),

    dismissAvailability: async (showId, ids, dismissed = true) => fetch(`/api/v1/shows/${showId}/availability/dismiss`, {
        method: 'POST',
        headers: await getAuthHeader(),
        body: JSON.stringify({ ids, dismissed }),
    }).then(handleResponse),

    addToAvailabilityPool: async (showId, data) => fetch(`/api/v1/shows/${showId}/availability/manual`, {
        method: 'POST',
        headers: await getAuthHeader(),
        body: JSON.stringify(data),
    }).then(handleResponse),

    // --- Shift board: shifts exist independently of who fills them, each with position
    // lines carrying a headcount ("Hands: 4") that assignments fill against. ---
    getShifts: async (showId, start = null, end = null) => {
        const params = new URLSearchParams();
        if (start) params.set('start', start);
        if (end) params.set('end', end);
        const qs = params.toString();
        return fetch(`/api/v1/shows/${showId}/shifts${qs ? `?${qs}` : ''}`, {
            headers: await getAuthHeader(),
        }).then(handleResponse);
    },

    createShift: async (showId, data) => fetch(`/api/v1/shows/${showId}/shifts`, {
        method: 'POST',
        headers: await getAuthHeader(),
        body: JSON.stringify(data),
    }).then(handleResponse),

    updateShift: async (shiftId, data) => fetch(`/api/v1/shifts/${shiftId}`, {
        method: 'PUT',
        headers: await getAuthHeader(),
        body: JSON.stringify(data),
    }).then(handleResponse),

    deleteShift: async (shiftId) => fetch(`/api/v1/shifts/${shiftId}`, {
        method: 'DELETE',
        headers: await getAuthHeader(),
    }).then(handleResponse),

    addShiftPosition: async (shiftId, data) => fetch(`/api/v1/shifts/${shiftId}/positions`, {
        method: 'POST',
        headers: await getAuthHeader(),
        body: JSON.stringify(data),
    }).then(handleResponse),

    updateShiftPosition: async (shiftId, positionId, data) => fetch(`/api/v1/shifts/${shiftId}/positions/${positionId}`, {
        method: 'PUT',
        headers: await getAuthHeader(),
        body: JSON.stringify(data),
    }).then(handleResponse),

    deleteShiftPosition: async (shiftId, positionId) => fetch(`/api/v1/shifts/${shiftId}/positions/${positionId}`, {
        method: 'DELETE',
        headers: await getAuthHeader(),
    }).then(handleResponse),

    // Assigns one roster member straight into an open shift position (the board's
    // per-position "assign" action).
    assignToPosition: async (shiftId, positionId, data) => fetch(`/api/v1/shifts/${shiftId}/positions/${positionId}/assign`, {
        method: 'POST',
        headers: await getAuthHeader(),
        body: JSON.stringify(data),
    }).then(handleResponse),

    updateShiftAssignment: async (assignmentId, data) => fetch(`/api/v1/shift-assignments/${assignmentId}`, {
        method: 'PUT',
        headers: await getAuthHeader(),
        body: JSON.stringify(data),
    }).then(handleResponse),

    deleteShiftAssignment: async (assignmentId) => fetch(`/api/v1/shift-assignments/${assignmentId}`, {
        method: 'DELETE',
        headers: await getAuthHeader(),
    }).then(handleResponse),

    updateShiftAssignmentStatus: async (assignmentId, status) => fetch(`/api/v1/shift-assignments/${assignmentId}/status`, {
        method: 'PUT',
        headers: await getAuthHeader(),
        body: JSON.stringify({ status }),
    }).then(handleResponse),

    // Account-wide Scheduling tab: every non-archived show's shift board/planning dates
    // in one call, grouped by show.
    getGlobalSchedule: async () => fetch('/api/v1/schedule/global', {
        headers: await getAuthHeader(),
    }).then(handleResponse),

    // Persisted "blank" planning days on the Schedule view — pinned to the
    // calendar independent of whether anyone's actually assigned to them yet.
    getScheduleDates: async (showId) => fetch(`/api/v1/shows/${showId}/schedule-dates`, {
        headers: await getAuthHeader(),
    }).then(handleResponse),

    addScheduleDate: async (showId, shiftDate, notes = null) => fetch(`/api/v1/shows/${showId}/schedule-dates`, {
        method: 'POST',
        headers: await getAuthHeader(),
        body: JSON.stringify({ shift_date: shiftDate, notes }),
    }).then(handleResponse),

    deleteScheduleDate: async (showId, shiftDate) => fetch(`/api/v1/shows/${showId}/schedule-dates/${shiftDate}`, {
        method: 'DELETE',
        headers: await getAuthHeader(),
    }).then(handleResponse),

    // Public, unauthenticated — the Decline click itself happens on the backend
    // (the email button links straight to it); Accept lands here so the person can
    // pick which dates actually work. No session exists for the person responding.
    getPublicAvailability: async (token) => fetch(`/api/public/availability/${token}`, {
        headers: { 'Content-Type': 'application/json' },
    }).then(handleResponse),

    respondToAvailability: async (token, dates) => fetch(`/api/public/availability/${token}/respond`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ dates }),
    }).then(handleResponse),

    // Public, unauthenticated — the crew-onboarding join form. No session exists for the
    // prospective crew member filling it out.
    getPublicOnboardingForm: async (slug) => fetch(`/api/public/join/${slug}`, {
        headers: { 'Content-Type': 'application/json' },
    }).then(handleResponse),

    submitOnboardingForm: async (slug, data) => fetch(`/api/public/join/${slug}/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
    }).then(handleResponse),

    // --- Crew Profile Links (public, no auth; the token is the credential) ---
    // `session` is the verified-code session, only when the owner requires an emailed code.
    getPublicProfile: async (token, session) => fetch(`/api/public/profile/${token}`, {
        headers: { 'Content-Type': 'application/json', ...(session ? { 'X-Profile-Session': session } : {}) },
    }).then(handleResponse),

    sendPublicProfileCode: async (token) => fetch(`/api/public/profile/${token}/code`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
    }).then(handleResponse),

    verifyPublicProfileCode: async (token, code) => fetch(`/api/public/profile/${token}/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code }),
    }).then(handleResponse),

    savePublicProfile: async (token, data, session) => fetch(`/api/public/profile/${token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(session ? { 'X-Profile-Session': session } : {}) },
        body: JSON.stringify(data),
    }).then(handleResponse),

    // --- User SMTP ---
    getUserSmtpSettings: async () => fetch('/api/user/smtp-settings', { headers: await getAuthHeader() }).then(handleResponse),
    
    createUserSmtpSettings: async (settings) => fetch('/api/user/smtp-settings', { 
        method: 'POST', 
        headers: await getAuthHeader(), 
        body: JSON.stringify(settings) 
    }).then(handleResponse),
    
    testUserSmtpSettings: async (settings) => fetch('/api/user/smtp-settings/test', {
        method: 'POST',
        headers: await getAuthHeader(),
        body: JSON.stringify(settings)
    }).then(handleResponse),

    // --- User Switch Defaults ---
    getSwitchDefaults: async () => fetch('/api/user/switch-defaults', { headers: await getAuthHeader() }).then(handleResponse),

    saveSwitchDefaults: async (settings) => fetch('/api/user/switch-defaults', {
        method: 'POST',
        headers: await getAuthHeader(),
        body: JSON.stringify(settings),
    }).then(handleResponse),

    // --- Timesheets ---
    getWeeklyTimesheet: async (showId, weekStartDate) => fetch(`/api/shows/${showId}/timesheet?week_start_date=${weekStartDate}`, { headers: await getAuthHeader() }).then(handleResponse),
    
    getShowBudget: async (showId) => fetch(`/api/shows/${showId}/budget`, { headers: await getAuthHeader() }).then(handleResponse),

    updateShowBudget: async (showId, budgetData) => fetch(`/api/shows/${showId}/budget`, {
        method: 'PUT',
        headers: await getAuthHeader(),
        body: JSON.stringify(budgetData)
    }).then(handleResponse),
    
    updateWeeklyTimesheet: async (showId, timesheetData) => fetch(`/api/shows/${showId}/timesheet`, { 
        method: 'PUT', 
        headers: await getAuthHeader(), 
        body: JSON.stringify(timesheetData) 
    }).then(handleResponse),
    
    getTimesheetPdf: async (showId, weekStartDate) => fetch(`/api/shows/${showId}/timesheet/pdf?week_start_date=${weekStartDate}`, { headers: await getAuthHeader() }).then(handleResponse),

    getCrewAuditPdf: async (showId, crewIds) => {
        const params = new URLSearchParams();
        crewIds.forEach(id => params.append('show_crew_ids', id));
        return fetch(`/api/shows/${showId}/timesheet/audit/pdf?${params.toString()}`, { headers: await getAuthHeader() }).then(handleResponse);
    },
    
    emailTimesheet: async (showId, weekStartDate, emailPayload) => fetch(`/api/shows/${showId}/timesheet/email?week_start_date=${weekStartDate}`, { 
        method: 'POST', 
        headers: await getAuthHeader(), 
        body: JSON.stringify(emailPayload) 
    }).then(handleResponse),

    // --- Contextual Notes ---
    getNotesForEntity: async (entityType, entityId, showId) => {
        let url = `/api/v1/notes/${entityType}/${entityId}`;
        if (showId) url += `?show_id=${showId}`;
        return fetch(url, { headers: await getAuthHeader() }).then(handleResponse);
    },
    
    createNote: async (noteData) => fetch('/api/v1/notes', { 
        method: 'POST', 
        headers: await getAuthHeader(), 
        body: JSON.stringify(noteData) 
    }).then(handleResponse),
    
    updateNote: async (noteId, updateData) => fetch(`/api/v1/notes/${noteId}`, { 
        method: 'PATCH', 
        headers: await getAuthHeader(), 
        body: JSON.stringify(updateData) 
    }).then(handleResponse),
    
    deleteNote: async (noteId) => fetch(`/api/v1/notes/${noteId}`, { method: 'DELETE', headers: await getAuthHeader() }).then(handleResponse),

    // --- Label Engine ---
    getLabelTemplates: async (category) => {
        const url = category ? `/api/v1/library/label-templates?category=${category}` : '/api/v1/library/label-templates';
        return fetch(url, { headers: await getAuthHeader() }).then(handleResponse);
    },

    getLabelTemplate: async (templateId) => {
        return fetch(`/api/v1/library/label-templates/${templateId}`, { headers: await getAuthHeader() }).then(handleResponse);
    },

    getLabelStocks: async () => {
        return fetch('/api/v1/library/label-stocks', { headers: await getAuthHeader() }).then(handleResponse);
    },
    
    createLabelTemplate: async (data) => {
        return fetch('/api/v1/library/label-templates', {
            method: 'POST',
            headers: await getAuthHeader(),
            body: JSON.stringify(data),
        }).then(handleResponse);
    },
    
    updateLabelTemplate: async (templateId, data) => {
        return fetch(`/api/v1/library/label-templates/${templateId}`, {
            method: 'PUT',
            headers: await getAuthHeader(),
            body: JSON.stringify(data),
        }).then(handleResponse);
    },
    
    deleteLabelTemplate: async (templateId) => {
        return fetch(`/api/v1/library/label-templates/${templateId}`, {
            method: 'DELETE',
            headers: await getAuthHeader(),
        }).then(handleResponse);
    },

    printLabels: async (showId, printData) => {
        return fetch(`/api/v1/shows/${showId}/label-engine/print`, {
            method: 'POST',
            headers: await getAuthHeader(),
            body: JSON.stringify(printData),
        }).then(handleResponse);
    },
        
    // --- Notifications ---
    getNotifications: async ({ unreadOnly = false, limit = 50, offset = 0 } = {}) => {
        const params = new URLSearchParams({ limit, offset });
        if (unreadOnly) params.append('unread_only', 'true');
        return fetch(`/api/v1/notifications?${params.toString()}`, { headers: await getAuthHeader() }).then(handleResponse);
    },

    getUnreadNotificationCount: async () => fetch('/api/v1/notifications/unread-count', { headers: await getAuthHeader() }).then(handleResponse),

    markNotificationRead: async (notificationId) => fetch(`/api/v1/notifications/${notificationId}/read`, {
        method: 'POST',
        headers: await getAuthHeader(),
    }).then(handleResponse),

    markAllNotificationsRead: async () => fetch('/api/v1/notifications/mark-all-read', {
        method: 'POST',
        headers: await getAuthHeader(),
    }).then(handleResponse),

    dismissNotification: async (notificationId) => fetch(`/api/v1/notifications/${notificationId}`, {
        method: 'DELETE',
        headers: await getAuthHeader(),
    }).then(handleResponse),

    clearAllNotifications: async () => fetch('/api/v1/notifications', {
        method: 'DELETE',
        headers: await getAuthHeader(),
    }).then(handleResponse),

    getNotificationPreferences: async () => fetch('/api/v1/notifications/preferences', { headers: await getAuthHeader() }).then(handleResponse),

    updateNotificationPreferences: async (prefs) => fetch('/api/v1/notifications/preferences', {
        method: 'PUT',
        headers: await getAuthHeader(),
        body: JSON.stringify(prefs),
    }).then(handleResponse),

    adminSendNotification: async (payload) => fetch('/api/v1/admin/notifications/send', {
        method: 'POST',
        headers: await getAuthHeader(),
        body: JSON.stringify(payload),
    }).then(handleResponse),

    adminGetSentNotifications: async () => fetch('/api/v1/admin/notifications/sent', { headers: await getAuthHeader() }).then(handleResponse),

    adminUnsendNotification: async (broadcastId) => fetch(`/api/v1/admin/notifications/broadcasts/${broadcastId}`, {
        method: 'DELETE',
        headers: await getAuthHeader(),
    }).then(handleResponse),

    // --- Legacy / Deprecated ---
    getDailyHours: async (showId) => fetch(`/api/shows/${showId}/daily_hours`, { headers: await getAuthHeader() }).then(handleResponse),
    
    bulkUpdateDailyHours: async (entries) => fetch('/api/daily_hours/bulk-update', { 
        method: 'POST', 
        headers: await getAuthHeader(), 
        body: JSON.stringify({ entries }), 
    }).then(handleResponse),
};